import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, ChevronLeft, ChevronRight, Minus, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

type BillingPeriod = "month" | "year" | "one_time";

interface PricingSettings {
  pageEyebrow: string;
  pageTitle: string;
  pageIntro: string;
  packagesEyebrow: string;
  packagesTitle: string;
  packagesIntro: string;
  packagesNote: string;
  detailsLinkText: string;
  configuratorEyebrow: string;
  configuratorTitle: string;
  configuratorIntro: string;
  configuratorNote: string;
  emptyConfiguratorText: string;
  monthlyEstimateLabel: string;
  annualEstimateLabel: string;
  oneTimeEstimateLabel: string;
  configuratorCtaLabel: string;
}

interface ManagedBundle {
  code: string;
  name: string;
  eyebrow: string;
  description: string;
  pricingPrefix: string;
  priceCents: number;
  billingPeriod: BillingPeriod;
  billingSuffix: string;
  featured: boolean;
  featuredLabel: string | null;
  ctaLabel: string;
  ctaPath: string;
  features: string[];
}

interface ConfiguratorItem {
  sku: string;
  name: string;
  description: string;
  category: string;
  unitPriceCents: number;
  billingPeriod: BillingPeriod;
  unitLabel: string;
  minimumQuantity: number;
  maximumQuantity: number;
  quantityStep: number;
}

interface PricingCatalog {
  settings: PricingSettings;
  bundles: ManagedBundle[];
  configuratorItems: ConfiguratorItem[];
}

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0
});

function formatMoney(cents: number) {
  return money.format(cents / 100);
}

function contactPath(path: string, key: string, value: string) {
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${new URLSearchParams({ service: "Managed services", [key]: value }).toString()}`;
}

function PricingDataState({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <section className="section pricing-section">
      <div className="container pricing-data-state" role="status">
        <p>{message}</p>
        {retry && <button type="button" className="button button-outline-dark" onClick={retry}>Try again</button>}
      </div>
    </section>
  );
}

function BundleCards({ bundles }: { bundles: ManagedBundle[] }) {
  const scroller = useRef<HTMLDivElement>(null);
  const hasCarousel = bundles.length > 3;

  function move(direction: -1 | 1) {
    scroller.current?.scrollBy({
      left: direction * scroller.current.clientWidth,
      behavior: "smooth"
    });
  }

  return (
    <div className={hasCarousel ? "pricing-carousel" : undefined}>
      {hasCarousel && (
        <div className="pricing-carousel-controls" aria-label="Managed-services package navigation">
          <button type="button" aria-label="Previous packages" onClick={() => move(-1)}><ChevronLeft /></button>
          <button type="button" aria-label="Next packages" onClick={() => move(1)}><ChevronRight /></button>
        </div>
      )}
      <div ref={scroller} className={hasCarousel ? "pricing-grid pricing-grid-scroll" : "pricing-grid"}>
        {bundles.map((bundle) => (
          <article className={bundle.featured ? "price-card featured" : "price-card"} key={bundle.code}>
            {bundle.featured && bundle.featuredLabel && <span className="popular">{bundle.featuredLabel}</span>}
            <span className="plan-eyebrow">{bundle.eyebrow}</span>
            <h3>{bundle.name}</h3>
            <div className="price">
              <small>{bundle.pricingPrefix}</small>
              <strong>{formatMoney(bundle.priceCents)}</strong>
              <span>{bundle.billingSuffix}</span>
            </div>
            <p>{bundle.description}</p>
            <ul>{bundle.features.map((feature) => <li key={feature}><Check /> {feature}</li>)}</ul>
            <Link
              to={contactPath(bundle.ctaPath, "plan", bundle.name)}
              className={bundle.featured ? "button button-primary" : "button button-outline-dark"}
            >
              {bundle.ctaLabel} <ArrowRight size={17} />
            </Link>
          </article>
        ))}
      </div>
    </div>
  );
}

function Configurator({ settings, items }: { settings: PricingSettings; items: ConfiguratorItem[] }) {
  const [selectedSku, setSelectedSku] = useState(items[0]?.sku || "");
  const [quantity, setQuantity] = useState(items[0]?.minimumQuantity || 1);
  const [lines, setLines] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!items.some((item) => item.sku === selectedSku)) {
      setSelectedSku(items[0]?.sku || "");
      setQuantity(items[0]?.minimumQuantity || 1);
    }
  }, [items, selectedSku]);

  const selectedItem = items.find((item) => item.sku === selectedSku);
  const configuredLines = useMemo(() => items
    .filter((item) => Boolean(lines[item.sku]))
    .map((item) => ({ item, quantity: lines[item.sku] })), [items, lines]);

  const totals = useMemo(() => configuredLines.reduce((sum, line) => {
    sum[line.item.billingPeriod] += line.item.unitPriceCents * line.quantity;
    return sum;
  }, { month: 0, year: 0, one_time: 0 } as Record<BillingPeriod, number>), [configuredLines]);

  function changeSelected(nextSku: string) {
    const item = items.find((candidate) => candidate.sku === nextSku);
    setSelectedSku(nextSku);
    setQuantity(item?.minimumQuantity || 1);
  }

  function clamp(item: ConfiguratorItem, value: number) {
    const stepped = Math.round(value / item.quantityStep) * item.quantityStep;
    return Math.min(item.maximumQuantity, Math.max(item.minimumQuantity, stepped));
  }

  function addItem() {
    if (!selectedItem) return;
    setLines((current) => ({
      ...current,
      [selectedItem.sku]: clamp(selectedItem, (current[selectedItem.sku] || 0) + quantity)
    }));
  }

  function updateLine(item: ConfiguratorItem, nextQuantity: number) {
    setLines((current) => ({ ...current, [item.sku]: clamp(item, nextQuantity) }));
  }

  function removeLine(sku: string) {
    setLines((current) => {
      const next = { ...current };
      delete next[sku];
      return next;
    });
  }

  const estimateSummary = configuredLines.length
    ? configuredLines.map(({ item, quantity: lineQuantity }) => `${lineQuantity} × ${item.name} (${item.sku})`).join(", ")
    : "Custom managed-services configuration";

  return (
    <section className="section configurator-section">
      <div className="container">
        <div className="section-heading center">
          <span className="eyebrow">{settings.configuratorEyebrow}</span>
          <h2>{settings.configuratorTitle}</h2>
          <p>{settings.configuratorIntro}</p>
        </div>

        {items.length === 0 ? (
          <div className="configurator-empty">{settings.emptyConfiguratorText}</div>
        ) : (
          <div className="configurator-shell">
            <div className="configurator-builder">
              <div className="configurator-picker">
                <label>
                  Managed service
                  <select value={selectedSku} onChange={(event) => changeSelected(event.target.value)}>
                    {items.map((item) => (
                      <option value={item.sku} key={item.sku}>
                        {item.name} — {formatMoney(item.unitPriceCents)}{item.unitLabel}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Quantity
                  <input
                    type="number"
                    min={selectedItem?.minimumQuantity || 1}
                    max={selectedItem?.maximumQuantity || 1000}
                    step={selectedItem?.quantityStep || 1}
                    value={quantity}
                    onChange={(event) => selectedItem && setQuantity(clamp(selectedItem, Number(event.target.value) || 1))}
                  />
                </label>
                <button type="button" className="button button-primary" onClick={addItem}><Plus size={18} /> Add service</button>
              </div>
              {selectedItem && <p className="selected-sku-description"><strong>{selectedItem.category}</strong> {selectedItem.description}</p>}

              <div className="configured-lines" aria-live="polite">
                {configuredLines.length === 0 ? (
                  <div className="configured-lines-empty">Choose a service and quantity to begin your estimate.</div>
                ) : configuredLines.map(({ item, quantity: lineQuantity }) => (
                  <div className="configured-line" key={item.sku}>
                    <div className="configured-line-copy">
                      <span>{item.category}</span>
                      <strong>{item.name}</strong>
                      <small>{item.sku} · {formatMoney(item.unitPriceCents)}{item.unitLabel}</small>
                    </div>
                    <div className="quantity-control" aria-label={`${item.name} quantity`}>
                      <button type="button" aria-label={`Decrease ${item.name}`} onClick={() => updateLine(item, lineQuantity - item.quantityStep)}><Minus /></button>
                      <input
                        aria-label={`${item.name} quantity`}
                        type="number"
                        min={item.minimumQuantity}
                        max={item.maximumQuantity}
                        step={item.quantityStep}
                        value={lineQuantity}
                        onChange={(event) => updateLine(item, Number(event.target.value) || item.minimumQuantity)}
                      />
                      <button type="button" aria-label={`Increase ${item.name}`} onClick={() => updateLine(item, lineQuantity + item.quantityStep)}><Plus /></button>
                    </div>
                    <strong className="configured-line-total">{formatMoney(item.unitPriceCents * lineQuantity)}</strong>
                    <button type="button" className="remove-line" aria-label={`Remove ${item.name}`} onClick={() => removeLine(item.sku)}><Trash2 /></button>
                  </div>
                ))}
              </div>
            </div>

            <aside className="estimate-card">
              <span>Managed-services estimate</span>
              <div><small>{settings.monthlyEstimateLabel}</small><strong>{formatMoney(totals.month)}</strong></div>
              {totals.year > 0 && <div><small>{settings.annualEstimateLabel}</small><strong>{formatMoney(totals.year)}</strong></div>}
              {totals.one_time > 0 && <div><small>{settings.oneTimeEstimateLabel}</small><strong>{formatMoney(totals.one_time)}</strong></div>}
              <p>{settings.configuratorNote}</p>
              <Link
                className="button button-primary"
                to={contactPath("/contact", "estimate", estimateSummary)}
              >
                {settings.configuratorCtaLabel} <ArrowRight size={17} />
              </Link>
            </aside>
          </div>
        )}
      </div>
    </section>
  );
}

export function ManagedPricing({ page = false, configurator = false }: { page?: boolean; configurator?: boolean }) {
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [error, setError] = useState("");
  const [requestKey, setRequestKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError("");
    fetch("/api/catalog", { signal: controller.signal, headers: { Accept: "application/json" } })
      .then(async (response) => {
        const body = await response.json() as PricingCatalog & { message?: string };
        if (!response.ok) throw new Error(body.message || "Managed-services pricing is temporarily unavailable.");
        return body;
      })
      .then(setCatalog)
      .catch((reason: unknown) => {
        if (reason instanceof DOMException && reason.name === "AbortError") return;
        setError(reason instanceof Error ? reason.message : "Managed-services pricing is temporarily unavailable.");
      });
    return () => controller.abort();
  }, [requestKey]);

  if (error) return <PricingDataState message={error} retry={() => setRequestKey((key) => key + 1)} />;
  if (!catalog) return <PricingDataState message="Loading current managed-services pricing…" />;

  const { settings, bundles, configuratorItems } = catalog;

  return (
    <>
      {page && (
        <section className="page-hero pricing-page-hero">
          <div className="page-hero-grid" />
          <div className="container page-hero-inner">
            <div>
              <span className="eyebrow">{settings.pageEyebrow}</span>
              <h1>{settings.pageTitle}</h1>
              <p>{settings.pageIntro}</p>
            </div>
          </div>
        </section>
      )}

      <section className="section pricing-section">
        <div className="container">
          <div className="section-heading center">
            <span className="eyebrow">{settings.packagesEyebrow}</span>
            <h2>{settings.packagesTitle}</h2>
            <p>{settings.packagesIntro}</p>
          </div>
          <BundleCards bundles={bundles} />
          <p className="pricing-note">{settings.packagesNote}</p>
          {!page && (
            <div className="center">
              <Link className="arrow-link" to="/pricing">{settings.detailsLinkText} <ArrowRight size={17} /></Link>
            </div>
          )}
        </div>
      </section>

      {configurator && <Configurator settings={settings} items={configuratorItems} />}
    </>
  );
}
